import React from 'react';
import { useNavigate } from 'react-router-dom';
import Profiledetail from './Profiledetail';
import Load from './Load';
import { AtletaData } from '../services/atletaService';
import { InscricaoTorneioResumo } from '../services/inscricaoTorneioService';
import { SeguidoItem } from '../services/seguidorService';
import { resolverExibicaoSeguido } from '../utils/seguidoDisplay';

interface UserPrincipalSidebarProps {
  atleta?: AtletaData['atletaPorId'];
  atletaLoading?: boolean;
  torneios: InscricaoTorneioResumo[];
  torneiosLoading: boolean;
  seguidos: SeguidoItem[];
  seguidosLoading: boolean;
  onVerTodosTorneios: () => void;
  onVerTodosSeguindo: () => void;
}

const UserPrincipalSidebar: React.FC<UserPrincipalSidebarProps> = ({
  atleta,
  atletaLoading,
  torneios,
  torneiosLoading,
  seguidos,
  seguidosLoading,
  onVerTodosTorneios,
  onVerTodosSeguindo,
}) => {
  const navigate = useNavigate();

  const formatTorneioData = (
    dataInicio?: string | null,
    dataFim?: string | null,
  ): string => {
    if (!dataInicio) {
      return '';
    }

    const [year, month, day] = dataInicio.split('-').map(Number);
    const inicio = new Date(year, month - 1, day);
    const inicioStr = inicio.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });

    if (dataFim) {
      const [yf, mf, df] = dataFim.split('-').map(Number);
      const fim = new Date(yf, mf - 1, df);
      const fimStr = fim.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      return `${inicioStr} - ${fimStr}`;
    }

    const inicioComAno = inicio.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return inicioComAno;
  };

  const renderTorneios = () => {
    const content = (() => {
      if (torneiosLoading) {
        return <Load />;
      }
      if (torneios.length === 0) {
        return (
          <div className="card-body pt-0 text-center">
            <p className="mb-0">Nenhum torneio encontrado.</p>
          </div>
        );
      }
      return (
        <div className="card-body pt-0">
          {torneios.map((inscricao) => {
            const torneio = inscricao.categoriaTorneio?.torneio;
            if (!torneio) return null;

            const cidade = torneio.local?.endereco?.cidade;
            const localStr = cidade
              ? `${cidade.nome}, ${cidade.estado.uf}`
              : torneio.local?.nome || '';
            const datasStr = formatTorneioData(torneio.dataInicio, torneio.dataFim);

            return (
              <div
                key={inscricao.id}
                className="d-flex align-items-center mb-3 hover-card rounded-3 p-2"
                style={{ cursor: 'pointer', marginLeft: '-0.5rem', marginRight: '-0.5rem' }}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/tournamentsingle/${torneio.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/tournamentsingle/${torneio.id}`);
                  }
                }}
              >
                <figure className="avatar me-3 mb-0">
                  <img
                    src={torneio.avatarUrl || '/assets/images/avatar.jpg'}
                    alt={torneio.nome}
                    className="rounded-circle"
                    style={{ width: 48, height: 48, objectFit: 'cover' }}
                  />
                </figure>
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-600 text-grey-900 font-xssss text-truncate">
                    {torneio.nome}
                  </div>
                  {inscricao.categoriaTorneio && (
                    <div className="font-xsssss text-grey-500 text-truncate">
                      {inscricao.categoriaTorneio.nome}
                    </div>
                  )}
                  <div className="font-xsssss text-grey-500">
                    {datasStr}
                  </div>
                  {localStr && (
                    <div className="font-xsssss text-grey-500 text-truncate">
                      {localStr}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    })();

    return (
      <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
        <div className="card-body d-flex align-items-center p-4">
          <h4 className="fw-700 mb-0 font-xssss text-grey-900">Torneios</h4>
          <button
            className="fw-600 ms-auto font-xssss text-primary btn btn-link p-0 border-0"
            onClick={onVerTodosTorneios}
          >
            Ver todos
          </button>
        </div>
        {content}
      </div>
    );
  };

  const renderSeguidos = () => {
    const content = (() => {
      if (seguidosLoading) {
        return <Load />;
      }
      if (seguidos.length === 0) {
        return (
          <div className="card-body pt-0 text-center">
            <p className="mb-0">Nenhum perfil seguido.</p>
          </div>
        );
      }
      return (
        <div className="card-body pt-0">
          <div className="row g-2">
            {seguidos.map((seguido) => {
              const { nome, username, imageUrl, path } = resolverExibicaoSeguido(seguido);
              const onClick = () => navigate(path);

              return (
                <div key={seguido.id} className="col-6">
                  <div
                    className="card w-100 shadow-xss rounded-xxl border-0 p-2 hover-card"
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={onClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick();
                      }
                    }}
                  >
                    <div className="d-flex flex-column align-items-center text-center">
                      <figure className="avatar mb-1">
                        <img
                          src={imageUrl || '/assets/images/avatar.jpg'}
                          alt={nome}
                          className="rounded-circle"
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                      </figure>
                      <div className="w-100 min-w-0" style={{ lineHeight: 1.2 }}>
                        <div className="fw-600 text-grey-900 font-xsssss text-truncate">{nome}</div>
                        {username && (
                          <div className="font-xsssss text-primary text-truncate">
                            @{username}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })();

    return (
      <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
        <div className="card-body d-flex align-items-center p-4">
          <h4 className="fw-700 mb-0 font-xssss text-grey-900">Seguindo</h4>
          <button
            className="fw-600 ms-auto font-xssss text-primary btn btn-link p-0 border-0"
            onClick={onVerTodosSeguindo}
          >
            Ver todos
          </button>
        </div>
        {content}
      </div>
    );
  };

  return (
    <>
      <Profiledetail atleta={atleta} loading={atletaLoading} />
      {renderTorneios()}
      {renderSeguidos()}
    </>
  );
};

export default UserPrincipalSidebar;
